import { AxiosError } from 'axios';
import { ApiResponseError } from './_models';

export interface FriendlyError {
  message: string;
  title?: string;
  code?: string;
}

/**
 * Maps Axios errors to user-friendly messages in Portuguese (pt-BR)
 */
export function mapAxiosErrorToFriendlyMessage(error: unknown): FriendlyError {
  // Handle ApiResponseError from our API
  if (error instanceof ApiResponseError) {
    // The 403 message is Spring's untranslated "Access Denied"; ours is the one the user can act on.
    if (error.status === 403) return mapHttpStatusToFriendlyError(403);
    return {
      message: error.getErrorMessage() || 'Ocorreu um erro na requisição.',
      code: String(error.status),
    };
  }

  // Handle Axios errors
  if (error instanceof AxiosError) {
    // Network error (no response received)
    if (error.code === 'ERR_NETWORK') {
      return {
        title: 'Erro de conexão',
        message: 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.',
        code: 'ERR_NETWORK',
      };
    }

    // Request timeout
    if (error.code === 'ECONNABORTED' || error.code === 'ERR_TIMEOUT') {
      return {
        title: 'Tempo esgotado',
        message: 'A requisição demorou muito para responder. Tente novamente.',
        code: error.code,
      };
    }

    // Request was cancelled
    if (error.code === 'ERR_CANCELED') {
      return {
        title: 'Requisição cancelada',
        message: 'A operação foi cancelada.',
        code: 'ERR_CANCELED',
      };
    }

    // Server responded with an error status
    if (error.response) {
      const status = error.response.status;
      const serverMessage = extractServerErrorMessage(error);

      return mapHttpStatusToFriendlyError(status, serverMessage);
    }

    // Request was made but no response received
    if (error.request) {
      return {
        title: 'Sem resposta',
        message: 'O servidor não respondeu. Tente novamente mais tarde.',
        code: 'NO_RESPONSE',
      };
    }

    // Error setting up the request
    return {
      title: 'Erro na requisição',
      message: error.message || 'Ocorreu um erro ao processar sua requisição.',
      code: 'REQUEST_SETUP_ERROR',
    };
  }

  // Handle generic Error objects
  if (error instanceof Error) {
    return {
      message: error.message || 'Ocorreu um erro inesperado.',
      code: 'UNKNOWN_ERROR',
    };
  }

  // Fallback for unknown error types
  return {
    message: 'Ocorreu um erro inesperado. Tente novamente.',
    code: 'UNKNOWN_ERROR',
  };
}

/**
 * Maps HTTP status codes to user-friendly error messages
 */
function mapHttpStatusToFriendlyError(status: number, serverMessage?: string): FriendlyError {
  const statusMessages: Record<number, FriendlyError> = {
    400: {
      title: 'Dados inválidos',
      message: serverMessage || 'Os dados enviados são inválidos. Verifique e tente novamente.',
      code: '400',
    },
    401: {
      title: 'Não autenticado',
      message: serverMessage || 'Sua sessão expirou. Faça login novamente.',
      code: '401',
    },
    403: {
      title: 'Acesso negado',
      message: serverMessage || 'Você não tem permissão para acessar este recurso. Peça acesso ao administrador.',
      code: '403',
    },
    404: {
      title: 'Não encontrado',
      message: serverMessage || 'O recurso solicitado não foi encontrado.',
      code: '404',
    },
    408: {
      title: 'Tempo esgotado',
      message: serverMessage || 'A requisição demorou muito. Tente novamente.',
      code: '408',
    },
    409: {
      title: 'Conflito',
      message: serverMessage || 'Já existe um registro com essas informações.',
      code: '409',
    },
    422: {
      title: 'Dados inválidos',
      message: serverMessage || 'Os dados enviados não puderam ser processados.',
      code: '422',
    },
    429: {
      title: 'Muitas requisições',
      message: serverMessage || 'Você fez muitas requisições. Aguarde um momento e tente novamente.',
      code: '429',
    },
    500: {
      title: 'Erro no servidor',
      message: serverMessage || 'Ocorreu um erro interno. Nossa equipe foi notificada.',
      code: '500',
    },
    502: {
      title: 'Servidor indisponível',
      message: serverMessage || 'O servidor está temporariamente indisponível. Tente novamente.',
      code: '502',
    },
    503: {
      title: 'Serviço indisponível',
      message: serverMessage || 'O serviço está em manutenção. Tente novamente mais tarde.',
      code: '503',
    },
    504: {
      title: 'Tempo esgotado',
      message: serverMessage || 'O servidor demorou para responder. Tente novamente.',
      code: '504',
    },
  };

  // Return specific message or generic one based on status range
  if (statusMessages[status]) {
    return statusMessages[status];
  }

  if (status >= 400 && status < 500) {
    return {
      title: 'Erro na requisição',
      message: serverMessage || 'Ocorreu um erro na sua requisição. Verifique os dados e tente novamente.',
      code: String(status),
    };
  }

  if (status >= 500) {
    return {
      title: 'Erro no servidor',
      message: serverMessage || 'Ocorreu um erro interno. Tente novamente mais tarde.',
      code: String(status),
    };
  }

  return {
    message: serverMessage || 'Ocorreu um erro inesperado.',
    code: String(status),
  };
}

/**
 * Extracts error message from server response
 */
function extractServerErrorMessage(error: AxiosError): string | undefined {
  const responseData = error.response?.data;

  if (!responseData) {
    return undefined;
  }

  // Handle our API response format
  if (typeof responseData === 'object' && responseData !== null) {
    const data = responseData as Record<string, unknown>;

    // Check for message field
    if (typeof data.message === 'string' && data.message) {
      return data.message;
    }

    // Check for error array (field validation errors)
    if (Array.isArray(data.error) && data.error.length > 0) {
      return data.error
        .map((err: { field?: string; message?: string }) =>
          err.field && err.message ? `${err.field}: ${err.message}` : err.message,
        )
        .filter(Boolean)
        .join('; ');
    }

    // Check for common error field names
    if (typeof data.error === 'string') {
      return data.error;
    }

    if (typeof data.errors === 'string') {
      return data.errors;
    }
  }

  // Handle string response
  if (typeof responseData === 'string') {
    return responseData;
  }

  return undefined;
}

/**
 * Gets a simple error message string from any error type
 * Useful for forms and simple error displays
 */
export function getErrorMessage(error: unknown): string {
  const friendlyError = mapAxiosErrorToFriendlyMessage(error);
  return friendlyError.message;
}

/**
 * Gets the error title if available, or returns a default title
 */
export function getErrorTitle(error: unknown, defaultTitle = 'Erro'): string {
  const friendlyError = mapAxiosErrorToFriendlyMessage(error);
  return friendlyError.title || defaultTitle;
}
